import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Drawer,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  Button,
  CircularProgress,
  Avatar,
  useTheme,
  useMediaQuery,
  Skeleton,
  Badge,
  Divider,
  Fade,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";


import {
  createConversation,
  listConversations,
  getMessages,
  sendMessage,
  deleteConversation,
  fetchUserStories,
  fetchStoryDetails,
} from "../utils/api";

import { t, Trans } from "@lingui/macro";


const TypingDots = () => (
  <Box sx={{ display: "inline-flex", gap: 0.5, alignItems: "center", px: 1 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "text.secondary",
          animation: "ai-bounce 1.2s infinite",
          animationDelay: `${i * 0.15}s`,
          "@keyframes ai-bounce": {
            "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.5 },
            "40%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
);

const Bubble = ({ sender, children, model }) => {
  const theme = useTheme();
  const isUser = sender === "user";
  const bg = isUser
    ? theme.palette.mode === "dark"
      ? theme.palette.primary.dark
      : theme.palette.primary.light
    : theme.palette.background.paper;
  const color = isUser
    ? theme.palette.getContrastText(bg)
    : theme.palette.text.primary;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isUser ? "row-reverse" : "row",
        alignItems: "flex-end",
        mb: 1.25,
        px: 0.5,
      }}
    >
      <Avatar
        sx={{
          bgcolor: isUser ? "primary.main" : "secondary.main",
          width: 28,
          height: 28,
          mx: 1,
        }}
      >
        {isUser ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
      </Avatar>
      <Box
        sx={{
          p: 1.25,
          borderRadius: 2,
          maxWidth: "75%",
          bgcolor: bg,
          color,
          boxShadow: isUser ? 1 : 0,
          border: isUser ? "none" : `1px solid ${theme.palette.divider}`,
          "& pre": { m: 0, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", whiteSpace: "pre-wrap" },
        }}
      >
        {isUser ? (
          <Typography sx={{ whiteSpace: "pre-wrap" }}>{children}</Typography>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
              code: ({ inline, children, ...props }) =>
                inline ? (
                  <code
                    style={{
                      background: "rgba(127,127,127,.15)",
                      padding: "0.1em 0.3em",
                      borderRadius: 4,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                ) : (
                  <pre
                    style={{
                      margin: "6px 0 8px",
                      padding: "10px 12px",
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 6,
                      overflowX: "auto",
                      background: "transparent",
                    }}
                    {...props}
                  >
                    <code style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                      {children}
                    </code>
                  </pre>
                ),
            }}
          >
            {children}
          </ReactMarkdown>
        )}
        {/* {model && (
          <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.7 }}>
            Model: {model}
          </Typography>
        )} */}
      </Box>
    </Box>
  );
};

const AICompanion = ({ getContext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // conversation list on mobile
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState(null);

  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(t`New chat`);
  const [newCreating, setNewCreating] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const [stories, setStories] = React.useState([]);
  const [selectedStoryId, setSelectedStoryId] = React.useState("");
  const [searchParams] = useSearchParams();
  const [storyTitleById, setStoryTitleById] = useState({});

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const toggleDrawer = () => setOpen((prev) => !prev);

  function normalizeId(v) {
    if (v == null) return "";
    const s = String(v).trim();
    return s && !/^(undefined|null)$/i.test(s) ? s : "";
  }

  const getInitialAssistantMessage = (storyId) => {
    const sid = normalizeId(storyId);
    if (sid) {
      const meta = stories.find((s) => s.id === sid);

      if (meta?.type === "interactive") {
        return t`Hey there! How can I help you today?`;
      }

      const title = storyTitleById[sid] || meta?.title;
      return title
        ? t`Hey there! How can I help you with the story titled: “${title}”?`
        : t`Hey there! How can I help you with this story?`;
    }
    return t`Hey there! How can I help you today?`;
  };


  useEffect(() => {
    if (open) {
      void loadConversations();
      setUnread(0);
    }
  }, [open]);

  useEffect(() => {
    // Auto scroll to bottom when messages change
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  useEffect(() => {
    const storyFromUrl = normalizeId(searchParams.get("storyId"));
    setSelectedStoryId(storyFromUrl);

    (async () => {
      try {
        const resp = await fetchUserStories();
        const list = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.stories)
            ? resp.stories
            : [];

        const normalized = list
          .map((s) => ({
            id: normalizeId(s?.id ?? s?.storyId ?? s?.storyID),
            title: s?.title ?? "Untitled",
            type: s?.type ?? s?.storyType ?? null,
          }))
          .filter((s) => s.id);

        setStories(normalized);
      } catch (e) {
        console.error("Failed to fetch user stories:", e);
      }
    })();

  }, [searchParams]);

  useEffect(() => {
    const missing = conversations
      .map((c) => normalizeId(c.storyId))
      .filter((id) => id && !storyTitleById[id]);

    if (missing.length === 0) return;

    (async () => {
      const entries = await Promise.all(
        Array.from(new Set(missing)).map(async (id) => {
          try {
            const s = await fetchStoryDetails(id);
            return [id, s?.title || "Unknown Story"];
          } catch {
            return [id, "Unknown Story"];
          }
        })
      );
      setStoryTitleById((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
  }, [conversations, storyTitleById]);

  const isInteractiveStory = (sid) => {
    const id = normalizeId(sid);
    if (!id) return false;
    const meta = stories.find((s) => s.id === id);
    return meta?.type === "interactive";
  };

  const loadConversations = async () => {
    try {
      setError(null);
      setLoadingConvs(true);
      const res = await listConversations();
      const normalized = (res.conversations || []).map((c) => {
        const sid = normalizeId(c.storyId ?? c.storyID);
        return { ...c, storyId: sid || undefined };
      });
      setConversations(normalized);

      if (!selected && normalized.length > 0) {
        handleSelect(normalized[0]);
      }

    } catch (e) {
      console.error("Failed to load conversations:", e);
      setError(t`Couldn't load chats. Try again.`);
    } finally {
      setLoadingConvs(false);
    }
  };

  const titleSuggestions = useMemo(() => {
    const date = new Date().toLocaleString();
    const base = [t`Notes ${date.split(",")[0]}`, t`Ideas ${date}`];
    return base;
  }, []);


  const handleSelect = async (conv) => {
    try {
      setSelected(conv);
      setLoadingMessages(true);
      const res = await getMessages(conv.id);
      const msgs = Array.isArray(res.messages) ? res.messages : [];
      setMessages(
        msgs.length > 0
          ? msgs
          : [{ sender: "assistant", content: getInitialAssistantMessage(conv.storyId) }]
      );
      if (mdDown) setSidebarOpen(false);
      if (!isMobile) setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      console.error("Failed to load messages:", e);
      setError(t`Couldn't load messages.`);
    } finally {
      setLoadingMessages(false);
    }
  };
  const handleNew = () => {
    setNewTitle(t`New chat`);

    const fromUrl = normalizeId(searchParams.get("storyId"));
    const fromCtx = normalizeId(
      getContext?.()?.storyId ?? getContext?.()?.storyID
    );

    let candidate = fromUrl || fromCtx || "";

    // 🔒 If that story is interactive → drop story context
    if (candidate && isInteractiveStory(candidate)) {
      candidate = "";
    }

    setSelectedStoryId(candidate);
    setNewOpen(true);
  };


  const handleCreateNew = async () => {
    if (!newTitle.trim()) return;
    setNewCreating(true);
    try {
      const context = (await getContext?.()) || {};

      let storyIdToSend = normalizeId(
        selectedStoryId || context.storyId || context.storyID
      );

      // 🔒 If the story is interactive, do NOT send it as context
      if (storyIdToSend && isInteractiveStory(storyIdToSend)) {
        storyIdToSend = "";
      }

      const payload = { title: newTitle.trim() };
      if (storyIdToSend) payload.storyId = storyIdToSend;
      if (context.storySummary) payload.summary = context.storySummary;

      const res = await createConversation(payload);

      const newConv = {
        id: res.conversationId,
        title: newTitle.trim(),
        ...(storyIdToSend ? { storyId: storyIdToSend } : {}),
      };

      setConversations((prev) => [newConv, ...prev]);
      setNewOpen(false);
      setNewTitle("");
      await handleSelect(newConv);
    } catch (e) {
      console.error("Failed to create conversation:", e);
      setError(t`Couldn't create a new conversation.`);
    } finally {
      setNewCreating(false);
    }
  };



  const handleDelete = async (convId) => {
    try {
      await deleteConversation(convId);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (selected?.id === convId) {
        setSelected(null);
        setMessages([]);
      }
    } catch (e) {
      console.error("Delete failed", e);
      setError(t`Couldn't delete conversation.`);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !selected || sending) return;
    const content = input.trim();
    setInput("");

    // optimistic user bubble
    setMessages((prev) => [...prev, { sender: "user", content }]);
    setSending(true);

    try {
      const res = await sendMessage({ conversationId: selected.id, content });
      setMessages((prev) => [
        ...prev,
        { sender: "assistant", content: res.reply, model: res.model },
      ]);
    } catch (e) {
      console.error("Message send failed:", e);
      // rollback-ish: show error reply
      setMessages((prev) => [
        ...prev,
        { sender: "assistant", content: t`Hmm, I couldn't send that. Try again?` },
      ]);
    } finally {
      setSending(false);
    }
  };

  // show a subtle top loading bar during loads/sends
  const showTopBar = loadingConvs || loadingMessages || sending;

  // side list content (becomes hidden behind a sidebar on mobile)
  const ConversationList = (
    <Box
      sx={{
        width: { xs: "100%", md: 240 },
        flexShrink: 0,
        borderRight: { xs: 0, md: `1px solid ${theme.palette.divider}` },
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box sx={{ p: 1, display: "flex", gap: 1 }}>
        <Button
          onClick={handleNew}
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          fullWidth
        >
          {t`New`}
        </Button>
        <Tooltip title={t`Refresh`}>
          <IconButton onClick={loadConversations} size="small">
            <RefreshIcon
              fontSize="small"
              sx={{ color: (theme) => theme.palette.primary.contrastText }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <Box sx={{ overflowY: "auto", flex: 1, px: 1 }}>
        {loadingConvs ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ py: 1 }}>
              <Skeleton variant="text" width="85%" />
              <Skeleton variant="text" width="55%" />
            </Box>
          ))
        ) : conversations.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, opacity: 0.7 }}>
            <Typography variant="body2" sx={{ p: 2, opacity: 0.7 }}>
              {t`No conversations yet.`}
            </Typography>
          </Typography>
        ) : (
          <List dense disablePadding>
            {conversations.map((conv) => {
              const selectedState = selected?.id === conv.id;
              return (
                <ListItem
                  key={conv.id}
                  disableGutters
                  sx={{
                    my: 0.25,
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                    borderLeft: '3px solid transparent',
                    transition: 'border-left-color .15s ease',
                    ...(selectedState && {
                      bgcolor: (t) =>
                        t.palette.action.selected +
                        (t.palette.mode === "dark" ? "22" : "11"),
                    }),
                    ...(selectedState && {
                      bgcolor: (t) =>
                        t.palette.action.selected +
                        (t.palette.mode === "dark" ? "22" : "11"),
                      borderLeftColor: 'secondary.main', // only change color, not width
                    }),

                  }}
                  onClick={() => handleSelect(conv)}
                  button
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      width: "100%",
                      minWidth: 0, // allow text to shrink
                    }}
                  >
                    {/* Text block */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 1,    // clamp to 2 lines
                          overflow: "hidden",
                          wordBreak: "break-word",
                          lineHeight: 1.25,
                        }}
                        title={conv.title || t`Untitled`}
                      >
                        {conv.title || t`Untitled`}
                      </Typography>
                      {!!conv.storyId && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 1,
                            overflow: "hidden",
                            mt: 0.25,
                          }}
                          title={storyTitleById[conv.storyId] || ""}
                        >
                          {storyTitleById[conv.storyId] || t`Loading story…`}
                        </Typography>
                      )}
                    </Box>

                    {/* Delete button stays fixed at end */}
                    <Tooltip title={t`Delete`}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete({ id: conv.id, title: conv.title || t`Untitled` });
                          setConfirmOpen(true);
                        }}
                        sx={{ ml: 0.5, color: (theme) => theme.palette.primary.contrastText }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box >
  );

  return (
    <>
      {/* Floating launcher — hides while the panel is open */}
      <Fade in={!open} unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: (t) => t.zIndex.drawer + 1,
          }}
        >
          <Badge color="secondary" badgeContent={unread} invisible={!unread}>
            <IconButton
              onClick={() => setOpen(true)}
              color="primary"
              size="large"
              sx={{
                width: 56,
                height: 56,
                boxShadow: 3,
                color: (t) => t.palette.primary.contrastText,
                bgcolor: (t) => t.palette.primary.main,
                transition: (t) =>
                  t.transitions.create(["transform", "background-color", "box-shadow"], {
                    duration: t.transitions.duration.shorter,
                  }),
                "&:hover": {
                  transform: "scale(1.06)",
                  bgcolor: (t) =>
                    t.palette.mode === "light"
                      ? darken(t.palette.primary.main, 0.08)
                      : lighten(t.palette.primary.main, 0.08),
                  boxShadow: 4,
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
              }}
            >
              <SmartToyIcon fontSize="large" />
            </IconButton>

          </Badge>
        </Box>
      </Fade>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", md: 720 },
            maxWidth: "100vw",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.default",
          },
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backdropFilter: "saturate(120%) blur(6px)",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          {mdDown && (
            <IconButton onClick={() => setSidebarOpen(true)} sx={{ mr: 1, color: (theme) => theme.palette.primary.contrastText }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {mdDown ? (
              <>
                <Typography
                  variant="h6"
                  noWrap
                  title={selected?.title || t`AI Companion`}
                >
                  {selected?.title || t`AI Companion`}
                </Typography>

                {!!selected?.storyId && !!storyTitleById[selected.storyId] && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    title={storyTitleById[selected.storyId]}
                    sx={{ display: "block" }}
                  >
                    {storyTitleById[selected.storyId]}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="h6">{t`AI Companion`}</Typography>
            )}
          </Box>


          <IconButton
            onClick={() => setOpen(false)}
            sx={{ color: (theme) => theme.palette.primary.contrastText }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* slim top loading bar */}
        {showTopBar && (
          <Box
            sx={{
              height: 2,
              width: "100%",
              bgcolor: "transparent",
              position: "absolute",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                left: "-40%",
                width: "40%",
                height: "100%",
                bgcolor: "primary.main",
                animation: "ai-bar 1.2s linear infinite",
              },
              "@keyframes ai-bar": {
                "0%": { left: "-40%" },
                "100%": { left: "100%" },
              },
            }}
          />
        )}

        <Box sx={{ display: "flex", flexGrow: 1, minHeight: 0 }}>
          {/* Sidebar (hidden under a drawer on mobile) */}
          {!mdDown && ConversationList}

          {/* Message area */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            {/* Empty state */}
            {!selected && !loadingConvs && (
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  placeItems: "center",
                  p: 3,
                  textAlign: "center",
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t`Start a conversation`}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                    {t`Create a new chat or pick one from the list.`}
                  </Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
                    {t`New Chat`}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Messages */}
            {selected && (
              <>
                <Box
                  sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    p: 2,
                    pt: 1.5,
                  }}
                >
                  {loadingMessages ? (
                    // skeleton bubbles
                    <>
                      {[...Array(5)].map((_, i) => (
                        <Box key={i} sx={{ display: "flex", mb: 1.5 }}>
                          <Avatar sx={{ width: 28, height: 28, mr: 1.5 }}>
                            <Skeleton variant="circular" width={28} height={28} />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Skeleton variant="rounded" width="70%" height={22} />
                            <Skeleton variant="rounded" width="55%" height={22} sx={{ mt: 0.5 }} />
                          </Box>
                        </Box>
                      ))}
                    </>
                  ) : (
                    messages.map((msg, idx) => (
                      <Bubble key={idx} sender={msg.sender} model={msg.model}>
                        {msg.content}
                      </Bubble>
                    ))
                  )}
                  {/* typing indicator */}
                  {sending && (
                    <Box sx={{ display: "flex", alignItems: "center", pl: 5, py: 0.5 }}>
                      <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor: "secondary.main" }}>
                        <SmartToyIcon fontSize="small" />
                      </Avatar>
                      <TypingDots />
                    </Box>
                  )}
                  <div ref={scrollRef} />
                </Box>

                {/* Composer */}
                <Box
                  sx={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    p: 1,
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-end",
                    position: "sticky",
                    bottom: 0,
                    bgcolor: "background.default",
                  }}
                >
                  <TextField
                    inputRef={inputRef}
                    fullWidth
                    size="small"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder={t`Ask something…`}
                    multiline
                    minRows={1}
                    maxRows={6}
                  />
                  <Tooltip title={t`Send`}>
                    <span>
                      <IconButton
                        onClick={handleSend}
                        disabled={sending || !input.trim()}
                        sx={{
                          alignSelf: "center",
                          bgcolor: !input.trim() ? "action.disabledBackground" : "primary.main",
                          color: !input.trim()
                            ? "action.disabled"
                            : theme.palette.getContrastText(theme.palette.primary.main),
                          "&:hover": {
                            bgcolor: !input.trim() ? "action.disabledBackground" : "primary.dark",
                          },
                        }}
                      >
                        {sending ? <CircularProgress size={18} /> : <SendIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Error toast-ish */}
        {error && (
          <Box
            sx={{
              position: "absolute",
              left: 16,
              bottom: 16,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              bgcolor: "error.main",
              color: (t) => t.palette.getContrastText(t.palette.error.main),
              boxShadow: 2,
            }}
            onClick={() => setError(null)}
          >
            {error}
          </Box>
        )}
      </Drawer>

      {/* Mobile: conversations slide-in */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        PaperProps={{
          sx: {
            width: Math.min(
              280,
              typeof window !== "undefined" ? window.innerWidth - 64 : 280
            ),
            bgcolor: "background.default",
            display: "flex",       // column layout so we can fix the header
            flexDirection: "column",
            height: "100%",
          },
        }}
      >
        {/* Header (non-scrollable) */}
        <Box sx={{ display: "flex", alignItems: "center", p: 1, flexShrink: 0 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {t`Chats`}
          </Typography>
          <IconButton
            onClick={() => setSidebarOpen(false)}
            sx={{ color: (theme) => theme.palette.primary.contrastText }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ flexShrink: 0 }} />

        {/* Body fills remaining space; only the inner list scrolls */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
          {ConversationList}
        </Box>
      </Drawer>

      <Dialog
        open={newOpen}
        onClose={() => {
          if (newCreating) return;
          setNewOpen(false);
          setNewTitle(t`New chat`);
          setSelectedStoryId("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t`New conversation`}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              label={t`Title`}
              placeholder={t`e.g., Outline for Chapter 3`}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newTitle.trim() && !newCreating) {
                  e.preventDefault();
                  handleCreateNew();
                }
              }}
              disabled={newCreating}
            />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {t`Or pick a quick title:`}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {titleSuggestions.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  onClick={() => setNewTitle(t)}
                  size="small"
                  variant="outlined"
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Stack>
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel id="story-select-label" shrink>
              {t`Story (optional)`}
            </InputLabel>
            <Select
              labelId="story-select-label"
              label={t`Story (optional)`}
              value={
                stories.some((s) => s.id === selectedStoryId)
                  ? selectedStoryId
                  : ""
              }
              onChange={(e) => setSelectedStoryId(normalizeId(e.target.value))}
              displayEmpty
              renderValue={(value) => {
                if (!value) return <em>{t`None`}</em>; // placeholder text when empty
                const found = stories.find((s) => s.id === value);
                return found ? found.title : value;
              }}
            >
              <MenuItem value="">
                <em>{t`None`}</em>
              </MenuItem>
              {stories.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} disabled={newCreating}>
            {t`Cancel`}
          </Button>
          <Button
            onClick={handleCreateNew}
            variant="contained"
            disabled={!newTitle.trim() || newCreating}
            startIcon={newCreating ? <CircularProgress size={16} /> : null}
          >
            {newCreating ? t`Creating…` : t`Create`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t`Delete conversation?`}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {pendingDelete?.title
              ? t`“${pendingDelete.title}” will be permanently removed.`
              : t`This conversation will be permanently removed.`}

          </Typography>
        </DialogContent>
        <DialogActions>
          {/* Cancel = contained CTA */}
          <Button
            variant="contained"
            onClick={() => setConfirmOpen(false)}
            disabled={deleting}
          >
            {t`Cancel`}
          </Button>

          {/* Delete = outlined CTA (no "error" color; uses your theme's CTA outlined) */}
          <Button
            variant="outlined"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
            onClick={async () => {
              const id = pendingDelete?.id;
              if (!id) return;
              setDeleting(true);
              await handleDelete(id);
              setDeleting(false);
              setConfirmOpen(false);
              setPendingDelete(null);
            }}
          >
            {deleting ? t`Deleting…` : t`Delete`}
          </Button>
        </DialogActions>

      </Dialog>


    </>
  );
};

export default AICompanion;
